import { Link } from "react-router-dom";
import type { Conversation } from "@/lib/chat";
import { timeAgo } from "@/lib/catalog";
import SmartMedia from "@/components/feature/SmartMedia";

interface ConversationListProps {
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  activeId?: string;
  onRetry: () => void;
}

export default function ConversationList({
  conversations,
  loading,
  error,
  activeId,
  onRetry,
}: ConversationListProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
      <div className="flex shrink-0 items-center justify-between border-b border-background-200/60 px-4 py-3">
        <h2 className="font-heading text-sm font-bold text-foreground-950">
          Conversaciones
        </h2>
        <button
          type="button"
          onClick={onRetry}
          aria-label="Actualizar mensajes"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            <p className="text-sm text-foreground-500">Cargando mensajes…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-error-warning-line text-xl text-accent-700"></i>
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground-900">
              Ocurrió un error
            </p>
            <p className="mt-1 max-w-xs text-xs text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Reintentar
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100">
              <i className="ri-chat-3-line text-xl text-foreground-400"></i>
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground-900">
              Aún no tenés mensajes
            </p>
            <p className="mt-1 max-w-xs text-xs text-foreground-500">
              Cuando contactes a un vendedor por una publicación, la conversación
              aparecerá acá.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {conversations.map((c) => {
              const active = c.id === activeId;
              return (
                <li key={c.id}>
                  <Link
                    to={`/mensajes/${c.id}`}
                    className={`flex cursor-pointer items-center gap-3 border-b border-background-200/50 px-4 py-3 transition-colors ${
                      active
                        ? "bg-primary-50"
                        : "hover:bg-background-100"
                    }`}
                  >
                    <span className="relative shrink-0">
                      {c.product_image ? (
                        <span className="block h-11 w-11 overflow-hidden rounded-full border border-background-200">
                          <SmartMedia
                            src={c.product_image}
                            alt={c.product_title}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                          {c.other_name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-foreground-950">
                          {c.other_name}
                        </span>
                        <span className="shrink-0 text-[11px] text-foreground-400">
                          {c.last_message_at
                            ? timeAgo(c.last_message_at)
                            : timeAgo(c.updated_at)}
                        </span>
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5">
                        <span
                          className={`truncate text-xs ${
                            c.unread_count > 0
                              ? "font-semibold text-foreground-900"
                              : "text-foreground-500"
                          }`}
                        >
                          {c.last_message_preview ?? "Nueva conversación"}
                        </span>
                        {c.unread_count > 0 && (
                          <span className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 px-1.5 text-[10px] font-bold leading-none text-white">
                            {c.unread_count > 99 ? "99+" : c.unread_count}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-foreground-400">
                        {c.product_title}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}